import { useCallback, useEffect, useState } from "react";
import { useInventoryFreshness } from "database/useDatabaseReader";
import { colorCodeRanking } from "helpers/stormtechColorCodes";
// Public share viewer: the CLAMPED read. This page is anonymous (App.jsx routes
// /public/sharing/:projID with no ProtectedRoute), so it must never receive
// oversell/backorder quantities. The unclamped getProductsByDisplayIds is for the
// authenticated project builder only.
import { getProductsByDisplayIdsPublic, groupByItemNumber } from "services/supabaseSearch";
import { processInventoryData } from "helpers/typesenseInventoryTransform";

const useProjectArticles = (articleIDs, warehouses) => {
  const [articlesData, setArticlesData] = useState([]);
  const [currentInventoryLoading, setCurrentInventoryLoading] = useState(false);
  const [currentInventory, setCurrentInventory] = useState({});

  const getArticlesData = useCallback(
    async (articleIDs, warehouses) => {
      setCurrentInventory([]);
      setCurrentInventoryLoading(true);

      const allIds = articleIDs.map((item) => item);
      const products = await getProductsByDisplayIdsPublic(allIds);
      const allGroupedHits = groupByItemNumber(products);

      const finalResult = [];

      allGroupedHits.forEach((group) => {
        const groupHits = group.colors;

        //Sort the colors
        const singleColor = [];
        const multipleColors = [];
        const clearanceColors = [];

        groupHits.forEach((hit) => {
          if (
            (hit.secondaryColorCode == null ||
              hit.primaryColorCode === hit.secondaryColorCode) &&
            !hit.isClearance
          ) {
            singleColor.push(hit);
          } else if (!hit.isClearance) {
            multipleColors.push(hit);
          } else {
            clearanceColors.push(hit);
          }
        });

        singleColor.sort(
          (a, b) =>
            colorCodeRanking.indexOf(a.primaryColorCode) -
            colorCodeRanking.indexOf(b.primaryColorCode),
        );

        multipleColors.sort(
          (a, b) =>
            colorCodeRanking.indexOf(a.primaryColorCode) -
              colorCodeRanking.indexOf(b.primaryColorCode) ||
            colorCodeRanking.indexOf(a.secondaryColorCode[0]) -
              colorCodeRanking.indexOf(b.secondaryColorCode[0]),
        );

        clearanceColors.sort(
          (a, b) =>
            colorCodeRanking.indexOf(a.primaryColorCode) -
            colorCodeRanking.indexOf(b.primaryColorCode),
        );

        const sortedGroupHits = [
          ...singleColor,
          ...multipleColors,
          ...clearanceColors,
        ];
        // console.log(sortedGroupHits);

        finalResult.push({
          itemNumber: group.itemNumber,
          colors: sortedGroupHits,
        });
      });

      setArticlesData(finalResult);

      // Process inventory data synchronously, then commit in ONE state update.
      // (Previously called setCurrentInventory once per colour, which is O(n^2)
      // object spreads and a render storm on large projects. Same final object,
      // built once.)
      const mergedInventory = {};
      finalResult.forEach(({ colors }) => {
        colors.forEach((colorData) => {
          Object.assign(
            mergedInventory,
            processInventoryData(colorData, warehouses).data,
          );
        });
      });
      setCurrentInventory((prevData) => ({ ...prevData, ...mergedInventory }));

      // console.log("Done getting inventory");
      // console.log(currentInventory);

      setCurrentInventoryLoading(false);
    },
    [],
  );

  // Memoize the refresh callback to prevent infinite re-renders
  const handleInventoryRefresh = useCallback(() => {
    // Only reload if we have the necessary data
    if (articleIDs?.length > 0 && warehouses?.length > 0) {
      console.log(`Auto-reloading project articles - fresh inventory data available`);
      getArticlesData(articleIDs, warehouses);
    }
  }, [articleIDs, warehouses, getArticlesData]);

  // Auto-reload when fresh inventory data is available (completion-based)
  const { isStale, minutesSinceUpdate } = useInventoryFreshness(handleInventoryRefresh, {}, articlesData); // Pass articlesData for public user timestamp-based refresh

  useEffect(() => {
    if (
      articleIDs == null ||
      articleIDs.length === 0 ||
      warehouses == null ||
      warehouses.length === 0
    )
      return;
    getArticlesData(articleIDs, warehouses);
  }, [articleIDs, getArticlesData, warehouses]);

  return {
    articlesData,
    currentInventory,
    currentInventoryLoading,
    isInventoryStale: isStale,
    minutesSinceInventoryUpdate: minutesSinceUpdate,
  };
};

export default useProjectArticles;
